import { Snackbar } from '@mui/material'
import moment from 'moment'
import React, { useRef, useState } from 'react'
import { Alert } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import Select from 'react-select'
import { FinancialReportItem } from 'store/settings/types'
import ViewRegisterModal from '../../components/Modal/ProfitLoss'
import { PeriodModeToggle, ReportPeriodMode, reportDatePickerProps } from '../../components/ReportPeriod/ReportPeriod'
import ScrollToggle from '../../components/ScrollToggle/ScrollToggle'
import CalculatingLoader from '../../components/CalculatingLoader/CalculatingLoader'
import { MONTHS, URLS } from '../../resources/constants'
import { fileDownloadBlob, lastYears, usdCurrencyFormat } from '../../resources/functions'
import { calculateGroupedTotals } from '../../resources/helper'
import { useReportApi } from '../../resources/hooks/api/useReportApiHook'
import { useSettingsApi } from '../../resources/hooks/api/useSettingsApiHook'
import {
  getBsRegenerationFailed,
  getBsRegenerationFailedMonth,
  getBsRegenerationFailedMsg,
  getIsQbdImport,
} from '../../store/settings/selectors'
import { getBranch } from '../../store/user/selectors'
import './BalanceSheet.css'

// The period a report was generated for. Headers, the View Register window and the PDF request
// read this snapshot, never the live filter controls.
type BsPeriod =
  | { mode: 'monthly'; month: string; year: number }
  | { mode: 'custom'; asOfDate: string } // YYYY-MM-DD

const isValidDate = (date: Date | null): date is Date => date instanceof Date && !isNaN(date.getTime())

// Statement hierarchy by report_code: GH / SH / SSH headers, VP / IP accounts, SST / ST / GT totals.
// A total's level matches the header it closes, so Total Bank Accounts lines up under Bank Accounts.
const ACCOUNT_CODES = ['VP', 'VP/IP', 'IP']
const HEADER_LEVELS: Record<string, number> = { GH: 0, SH: 1, SSH: 2 }
const TOTAL_LEVELS: Record<string, number> = { GT: 0, ST: 1, SST: 2 }
const ROW_VARIANTS: Record<string, string> = {
  GH: 'group',
  SH: 'section',
  SSH: 'subsection',
  SST: 'subtotal',
  ST: 'section-total',
  GT: 'grand-total',
}

type BsRow = { data: FinancialReportItem; code: string; level: number }

// Negatives in parentheses; anything that rounds to zero cents is shown as a muted $ 0.00
const formatAmount = (value: number | null) => {
  const amount = value || 0
  if (Math.round(amount * 100) === 0) return { text: usdCurrencyFormat(0), isZero: true }
  return { text: amount < 0 ? `(${usdCurrencyFormat(Math.abs(amount))})` : usdCurrencyFormat(amount), isZero: false }
}

const BalanceSheet: React.FC = () => {
  const { balanceSheetData, balanceSheetRefresh, regenerateBalanceSheet, downloadReport } =
    useReportApi()
  const { fetchAccountSettingsData } = useSettingsApi()
  const { t } = useTranslation(['scenes.balanceSheet','resources.form-validator'])

  const [isNotify, setIsNotify] = useState<boolean>(false)
  const [isBalanced, setIsBalanced] = useState<boolean>(true)
  const [balanceDifference, setBalanceDifference] = useState<number>(0)
  const [firstImbalancedMonth, setFirstImbalancedMonth] = useState<number | null>(null)
  const [firstImbalancedYear, setFirstImbalancedYear] = useState<number | null>(null)
  const [unreconciledPeriods, setUnreconciledPeriods] = useState<any[]>([])
  // True when a user-initiated Regenerate completed but the sheet is STILL imbalanced
  // (i.e. a data issue regeneration can't fix). regenVerifyPendingRef marks that the
  // next balance-sheet fetch follows such a Regenerate and should be evaluated.
  const [dataIssueAfterRegen, setDataIssueAfterRegen] = useState<boolean>(false)
  const regenVerifyPendingRef = useRef<boolean>(false)
  const [glBalanceSheetList, setGlBalanceSheetList] = useState<FinancialReportItem[]>([])
  const [searchParams, setSearchParams] = useState<any>({
    month: { value: '01', label: t('month.01', { ns: 'resources.form-validator' }) },
    year: {
      value: new Date().getFullYear(),
      label: '' + new Date().getFullYear(),
    },
  })
  const [periodMode, setPeriodMode] = useState<ReportPeriodMode>('monthly')
  const [asOfDate, setAsOfDate] = useState<Date | null>(null)
  const [generatedPeriod, setGeneratedPeriod] = useState<BsPeriod | null>(null)
  // true only after a Generate completed successfully with no report rows; before that the empty
  // table shows the neutral "select a period" prompt instead of a no-data message
  const [lastGenerateEmpty, setLastGenerateEmpty] = useState<boolean>(false)
  const periodModeOptions: { value: ReportPeriodMode; label: string }[] = [
    { value: 'monthly', label: t('filter.period_mode.monthly') },
    { value: 'custom', label: t('filter.period_mode.custom') },
  ]
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const [isThirdPartyEnabled, setIsThirdPartyEnabled] = useState<boolean>(false)
  const [isShowViewRegister, setIsShowViewRegister] = useState<boolean>(false)
  const [txnRegisterPayload, setTxnRegisterPayload] = useState<{
    startDate: string
    endDate: string
    account: number
    hasEditAccess: boolean
  }>({
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    account: 0,
    hasEditAccess: false,
  })
  // Column headers and page-header period lines for the generated report. Monthly compares month-ends; custom prior
  // year = same calendar date one year earlier (moment clamps Feb 29 to Feb 28), matching the
  // backend's prior_as_of_date.
  const generatedHeaders = (() => {
    if (!generatedPeriod) return null
    const isCustom = generatedPeriod.mode === 'custom'
    const current = isCustom
      ? moment(generatedPeriod.asOfDate, 'YYYY-MM-DD')
      : moment(`${generatedPeriod.year}-${generatedPeriod.month}-01`, 'YYYY-MM-DD').endOf('month')
    const prior = isCustom ? current.clone().subtract(1, 'year') : current.clone().subtract(1, 'year').endOf('month')
    return {
      current: {
        label: isCustom ? t('table.header.as_of_date') : t('table.header.current_month'),
        date: current.format('MM/DD/YYYY'),
      },
      prior: { label: t('table.header.prior_year_short'), date: prior.format('MM/DD/YYYY') },
      title: t('table.header.as_of', { date: current.format('LL') }),
      subtitle: t('table.compared_with', { date: prior.format('LL') }),
    }
  })()

  // View Register window. Its opening/ending balances are read per calendar month from the stored
  // monthly rows, so only a month window matches them: monthly mode, or a custom as_of_date that is
  // a month-end (same window as monthly). A mid-month as_of_date has no matching window - not offered.
  const registerMonth = (() => {
    if (!generatedPeriod) return null
    if (generatedPeriod.mode === 'monthly') return { year: generatedPeriod.year, month: generatedPeriod.month }
    const asOf = moment(generatedPeriod.asOfDate, 'YYYY-MM-DD')
    return asOf.date() === asOf.daysInMonth() ? { year: asOf.year(), month: asOf.format('MM') } : null
  })()
  const branchData = useSelector(getBranch)
  const branchId = +branchData['id']
 
  const isQbdImport = useSelector(getIsQbdImport)
  const bsRegenerationFailed = useSelector(getBsRegenerationFailed)
  const bsRegenerationFailedMonth = useSelector(getBsRegenerationFailedMonth)
  const bsRegenerationFailedMsg = useSelector(getBsRegenerationFailedMsg)
  const showRegenerationFailedBanner =
    isQbdImport && bsRegenerationFailed
  const tableContainerRef = useRef<HTMLDivElement>(null)
  // Collapsed Section / Sub-section headers by account_chart id (view-only state)
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set())

  const _toggleCollapsed = (id: number) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Display rows: zero-balance accounts hidden as before, indent level taken from the nearest header,
  // and rows under a collapsed header skipped up to its matching total (which stays visible).
  const reportRows = (() => {
    const rows: BsRow[] = []
    let headerLevel = 0
    let collapsedLevel: number | null = null
    glBalanceSheetList.forEach((data) => {
      const code = data['account_chart']['report_code']
      if (ACCOUNT_CODES.includes(code) && !data['prev_year_total_amount'] && !data['total_amount']) return
      const header = HEADER_LEVELS[code]
      const total = TOTAL_LEVELS[code]
      if (collapsedLevel !== null) {
        const boundary = header ?? total
        if (boundary === undefined || boundary > collapsedLevel) return
        collapsedLevel = null
      }
      let level = headerLevel + 1
      if (header !== undefined) {
        level = header
        headerLevel = header
        if (header > 0 && collapsedIds.has(data['account_chart']['id'])) collapsedLevel = header
      } else if (total !== undefined) {
        level = total
        headerLevel = Math.max(total - 1, 0)
      }
      rows.push({ data, code, level })
    })
    return rows
  })()

  // Filter controls are a draft for the NEXT Generate; the visible report stays tied to
  // generatedPeriod until a Generate succeeds. draftPeriod is null while the custom date is missing/invalid.
  const draftPeriod: BsPeriod | null =
    periodMode === 'custom'
      ? isValidDate(asOfDate) ? { mode: 'custom', asOfDate: moment(asOfDate).format('YYYY-MM-DD') } : null
      : { mode: 'monthly', month: searchParams.month['value'], year: searchParams.year['value'] }

  // Simple input checks before Generate (typed input included); the backend stays authoritative
  const draftPeriodError = (): string | null => {
    if (periodMode !== 'custom') return null
    if (!asOfDate) return 'filter.as_of_date_required'
    if (!isValidDate(asOfDate)) return 'filter.date_invalid'
    if (moment(asOfDate).isAfter(moment(), 'day')) return 'filter.date_in_future'
    return null
  }

  // Generate (no argument) validates and sends the draft. Closing the View Register re-fetches
  // the generated period it was opened from, never the draft.
  const fetchBalanceSheetList = (periodToFetch?: BsPeriod) => {
    if (!periodToFetch) {
      const draftError = draftPeriodError()
      if (draftError) {
        toast.error(t(draftError))
        return
      }
    }
    const requestedPeriod = (periodToFetch ?? draftPeriod) as BsPeriod
    const params: any =
      requestedPeriod.mode === 'custom'
        ? {
            page: 1,
            per_page: 5000,
            as_of_date: requestedPeriod.asOfDate,
            branch_id: +branchData['id'],
          }
        : {
            page: 1,
            per_page: 5000,
            year: requestedPeriod.year,
            month: requestedPeriod.month,
            branch_id: +branchData['id'],
          }

    setIsLoading(true)
    balanceSheetData(
      params,
      (message: string, resp: any) => {

        if (
          resp &&
          resp['data'] &&
          resp['data']['data'] &&
          resp['data']['data']['result']
        ) {
          setGeneratedPeriod(requestedPeriod)
          const calculatedData = calculateGroupedTotals(resp.data.data.result)
          setLastGenerateEmpty(!calculatedData.length)
          const isThirdPartyEnabled =
            resp.data.data?.is_third_party_enable ?? false

          setGlBalanceSheetList(calculatedData)
          // setGlBalanceSheetList(resp['data']['data']['result'])
          // if (isThirdPartyEnabled) {
          //     setGlBalanceSheetList(resp['data']['data']['result'])
          // } else {
          //     setGlBalanceSheetList(calculatedData)
          // }
          setIsNotify(resp.data.data?.is_notify || false)
          const balanced = resp.data.data?.is_balanced !== false
          setIsBalanced(balanced)
          setBalanceDifference(resp.data.data?.balance_difference || 0)
          setFirstImbalancedMonth(resp.data.data?.first_imbalanced_month || null)
          setFirstImbalancedYear(resp.data.data?.first_imbalanced_year || null)
          setUnreconciledPeriods(resp.data.data?.unreconciled_periods || [])
          // If this fetch follows a user-initiated Regenerate, a still-imbalanced
          // sheet means regeneration couldn't fix it — i.e. a data issue.
          if (regenVerifyPendingRef.current) {
            setDataIssueAfterRegen(!balanced)
            regenVerifyPendingRef.current = false
          } else if (balanced) {
            setDataIssueAfterRegen(false)
          }
          setIsLoading(false)

          setIsNotify(resp.data.data?.is_notify || false)
          setIsThirdPartyEnabled(isThirdPartyEnabled)
        } else {
          setGlBalanceSheetList([])
          setGeneratedPeriod(null)
          setLastGenerateEmpty(true)
          setIsLoading(false)
        }
      },
      (message: string, resp: any) => {
        setGlBalanceSheetList([])
        setGeneratedPeriod(null)
        setLastGenerateEmpty(false)
        setIsLoading(false)
        toast.error(message)
      }
    )
  }

  const handleRetryFailedRegeneration = () => {
    if (!bsRegenerationFailedMonth) return
    const [failedYear, failedMonth] = bsRegenerationFailedMonth.split('-').map(Number)
    if (!failedYear || !failedMonth) return

    regenVerifyPendingRef.current = true
    setDataIssueAfterRegen(false)
    setIsLoading(true)
    regenerateBalanceSheet(
      { year: failedYear, month: failedMonth, branch_id: +branchData['id'] },
      (message: string, resp: any) => {
        setIsLoading(false)
        toast.success(resp?.data?.message || t('alerts.regenStarted'))
        getSettingsData()
      },
      (message: string, resp: any) => {
        setIsLoading(false)
        toast.error(message || t('alerts.regenFailed'))
      }
    )
  }

  const refreshBalanceSheetList = () => {
    const params: any = {
      page: 1,
      per_page: 5000,
      year: searchParams.year['value'],
      month: searchParams.month['value'],
      branch_id: +branchData['id'],
    }

    setIsLoading(true)
    balanceSheetRefresh(
      params,
      (message: string, resp: any) => {
        setIsLoading(false)
        if (
          resp &&
          resp['data'] &&
          resp['data']['data'] &&
          resp['data']['data']['result']
        ) {
          setGlBalanceSheetList(resp.data.data.result)
        } else {
          setGlBalanceSheetList([])
        }

        if (
          resp &&
          resp['data'] &&
          resp['data']['data'] &&
          resp['data']['data']
        ) {
          setIsNotify(resp.data.data?.is_notify || false)
        }
      },
      (message: string, resp: any) => {
        setGlBalanceSheetList([])
        setIsLoading(false)
        toast.error(message)
      }
    )
  }

  // Backend PDF (Jinja2 + WeasyPrint, searchable text) for the generated period
  const _downloadReport = () => {
    if (!generatedPeriod) {
      return
    }
    const params: any =
      generatedPeriod.mode === 'custom'
        ? { as_of_date: generatedPeriod.asOfDate, branch_id: +branchData['id'], type: 1 }
        : { year: generatedPeriod.year, month: +generatedPeriod.month, branch_id: +branchData['id'], type: 1 }
    const fileName =
      generatedPeriod.mode === 'custom'
        ? `balance-sheet-as-of-${generatedPeriod.asOfDate}.pdf`
        : `balance-sheet-${moment().format('YYYY-MM-DD')}.pdf`

    setIsDownloading(true)
    downloadReport(
      params,
      (message: string, resp: any) => {
        setIsDownloading(false)
        fileDownloadBlob(resp, fileName)
      },
      (message: string, resp: any) => {
        setIsDownloading(false)
        toast.error(message)
      }
    )
  }

  // Switching Monthly <-> Custom Date changes the report's presentation, so the generated report
  // is cleared and Export waits for a Generate in the new mode.
  const _handleChangePeriodMode = (mode: ReportPeriodMode) => {
    setDataIssueAfterRegen(false)
    setGlBalanceSheetList([])
    setGeneratedPeriod(null)
    setLastGenerateEmpty(false)
    setPeriodMode(mode)
  }

  // Date / month / year edits only change the draft: the visible report, its headers, View Register
  // and Export stay on generatedPeriod until Generate succeeds. No report request is sent here.
  const _handleChangeAsOfDate = (date: Date | null) => {
    setAsOfDate(date)
  }

  const _handleChangeSearchParam = (e: any, type: string) => {
    if (type === 'month') {
      setSearchParams({
        ...searchParams,
        month: e,
      })
    } else if (type === 'year') {
      setSearchParams({
        ...searchParams,
        year: e,
      })
    }
  }

  const getSettingsData = (onDone?: () => void) => {
    fetchAccountSettingsData(
      { branch_id: branchId },
      (msg: string, resp: any) => {
        onDone?.()
      },
      (msg: string, resp: any) => {
        onDone?.()
      }
    )
  }

  const getLastDayOfMonth = (month: any, year: any) => {
    return new Date(year, month, 0).getDate()
  }

  const _handleShowViewRegister = (data: any, hasEditAccess: boolean) => {
    if (!registerMonth) {
      return
    }
    setTxnRegisterPayload({
      startDate: `${registerMonth.year}-${registerMonth.month}-01`,
      endDate: `${registerMonth.year}-${
        registerMonth.month
      }-${getLastDayOfMonth(
        registerMonth.month,
        registerMonth.year
      )}`,
      account: data['account_chart']['id'],
      hasEditAccess: isThirdPartyEnabled ? false : hasEditAccess,
    })
    setIsShowViewRegister(true)
  }

  const _handleCloseViewRegister = () => {
    setIsShowViewRegister(false)
    // Editing/adding transactions in the register changes the underlying data and
    // triggers a fresh auto-cascade, so any prior "data issue" verdict is now stale.
    setDataIssueAfterRegen(false)
    if (generatedPeriod) {
      fetchBalanceSheetList(generatedPeriod)
    }
  }

  const renderAmount = (amount: { text: string; isZero: boolean }, onClick?: () => void) =>
    onClick ? (
      <button
        type="button"
        onClick={onClick}
        className={`bs-amount bs-amount--link${amount.isZero ? ' bs-amount--zero' : ''}`}
      >
        {amount.text}
      </button>
    ) : (
      <span className={`bs-amount${amount.isZero ? ' bs-amount--zero' : ''}`}>{amount.text}</span>
    )

  const hasReport = !!(glBalanceSheetList.length && generatedPeriod)

  return (
    <div className="main-container flex-grow-1 bs-page">
      <div className="container-fluid">
        <div className="bs-page-header">
          <div className="bs-title-block">
            <div className="bs-title-icon" aria-hidden="true">
              <i className="fa-solid fa-scale-balanced"></i>
            </div>
            <div>
              <p className="bs-eyebrow">{t('eyebrow')}</p>
              <h1 className="bs-title">{t('title')}</h1>
              {/* Period of the report on screen (generatedPeriod), never the draft filters */}
              {generatedHeaders ? (
                <div className="bs-period">
                  <p className="bs-period__primary">{generatedHeaders.title}</p>
                  <p className="bs-period__secondary">{generatedHeaders.subtitle}</p>
                </div>
              ) : (
                <p className="bs-subtitle">{t('subtitle')}</p>
              )}
            </div>
          </div>
          <div className="report-period-toolbar bs-toolbar d-flex align-items-center flex-wrap">
            <PeriodModeToggle
              options={periodModeOptions}
              value={periodMode}
              onChange={_handleChangePeriodMode}
              ariaLabel={t('title')}
            />
            <span className="bs-toolbar-divider" aria-hidden="true"></span>
            <div className="report-period-fields d-flex align-items-center gap-2">
              {periodMode === 'monthly' ? (
                <>
                  <div className="filter-box report-period-field">
                    <Select
                      onChange={(e: any) => _handleChangeSearchParam(e, 'month')}
                      options={MONTHS}
                      defaultValue={searchParams['month']}
                      className={'zindex-10'}
                      classNamePrefix="report-period-select"
                      aria-label={t('filter.month')}
                    />
                  </div>
                  <div className="filter-box report-period-field report-period-field--year">
                    <Select
                      onChange={(e: any) => _handleChangeSearchParam(e, 'year')}
                      options={lastYears(5)}
                      defaultValue={searchParams['year']}
                      className={'zindex-10'}
                      classNamePrefix="report-period-select"
                      aria-label={t('filter.year')}
                    />
                  </div>
                </>
              ) : (
                <div className="filter-box report-period-field bs-date-field">
                  <DatePicker
                    selected={asOfDate}
                    onChange={(date: Date | null) => _handleChangeAsOfDate(date)}
                    className="form-control"
                    dateFormat={'MM/dd/yyyy'}
                    placeholderText={t('filter.as_of_date')}
                    maxDate={new Date()}
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={40}
                    showMonthDropdown
                    {...reportDatePickerProps}
                  />
                  <i className="fa-regular fa-calendar bs-date-field__icon" aria-hidden="true"></i>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => fetchBalanceSheetList()}
              className="btn btn-primary bs-toolbar-btn"
            >
              <i className="fa-solid fa-play" aria-hidden="true"></i>
              {t('filter.generate_report')}
            </button>
            {!isThirdPartyEnabled && glBalanceSheetList.length ? (
              <button
                type="button"
                onClick={refreshBalanceSheetList}
                className="btn-warning btn btn-lg d-none"
              >
                {t('filter.refresh')} &nbsp;{' '}
                <i className="fa fa-refresh" aria-hidden="true"></i>
              </button>
            ) : (
              <></>
            )}
            {/* Always rendered (disabled until a report exists) so the toolbar width never changes */}
            <span title={hasReport ? undefined : t('filter.export_pdf_hint')}>
              <button
                type="button"
                onClick={_downloadReport}
                disabled={isDownloading || !hasReport}
                className="btn bs-toolbar-btn bs-btn-export"
              >
                {isDownloading ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  <i className="fa-solid fa-download" aria-hidden="true"></i>
                )}
                {t('filter.export_pdf')}
              </button>
            </span>
          </div>
        </div>
        {/* Fixed full-page overlay; kept outside the toolbar so it takes no flex slot */}
        <div className="filter-btn">
          <div className={`page-loader ${isLoading ? 'visible' : 'hidden'}`}>
            <CalculatingLoader variant="primary" size={40} label={t('loader.generating_report')} />
          </div>
        </div>
        {
          // !isThirdPartyEnabled && isNotify ? (
          //     <Alert variant={'info'}>
          //         <i className="fa fa-exclamation-triangle fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;
          //         Please refresh the balance sheet to reflect new updates.
          //     </Alert>
          // ) : <></>
        }
        {showRegenerationFailedBanner && (
          <Snackbar
            open={true}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            autoHideDuration={null}
          >
            <Alert variant={'danger'} className="d-flex align-items-center justify-content-between p-2">
              <div>
                <i className="fa fa-exclamation-triangle fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;
                <strong>Report regeneration stopped</strong>{' '}
                {bsRegenerationFailedMonth
                  ? `Balance sheet regeneration stopped at ${new Date(
                      +bsRegenerationFailedMonth.split('-')[0],
                      +bsRegenerationFailedMonth.split('-')[1] - 1
                    ).toLocaleString('en-US', { month: 'long', year: 'numeric' })} after repeated failures -- some months may show outdated data.`
                  : 'Balance sheet regeneration stopped after repeated failures -- some months may show outdated data.'}
                {bsRegenerationFailedMsg && (
                  <div style={{ fontSize: '0.85em', marginTop: 4, opacity: 0.8 }}>
                    {bsRegenerationFailedMsg}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={handleRetryFailedRegeneration}
                style={{ marginLeft: '10px' }}
              >
                <i className="fa fa-refresh" aria-hidden="true"></i>&nbsp;
                Retry
              </button>
            </Alert>
          </Snackbar>
        )}
        {!isBalanced && (glBalanceSheetList.length > 0) && (
          dataIssueAfterRegen ? (
            <Snackbar
              open={true}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={null}
              sx={{
                width: '100%',
              }}
            >
              <Alert variant={'danger'} className="d-flex align-items-center p-2">
                <div>
                  <i className="fa fa-exclamation-triangle fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;
                    {t('alerts.imbalanceStillTitle', {
                    difference: balanceDifference.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }),
                  })}
                  {firstImbalancedMonth && firstImbalancedYear && (
                    <>
                      {' '}
                      <Link
                        to={`${URLS.VIEW_REGISTER}?month=${firstImbalancedMonth}&year=${firstImbalancedYear}`}
                        style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
                      >
                        {t('alerts.reviewTransactionsLink', {
                          date: new Date(firstImbalancedYear, firstImbalancedMonth - 1).toLocaleString('en-US', {
                            month: 'long',
                            year: 'numeric',
                          }),
                        })}
                      </Link>
                    </>
                  )}
                </div>
              </Alert>
            </Snackbar>
          ) : (
            <Snackbar
              open={true}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={null}
                sx={{
                width: '100%',
              }}
              // onClose={() => setShowSnackbar(false)}
            >
              <Alert variant={'warning'} className="d-flex align-items-center justify-content-between p-2">
                <div>
                  <i className="fa fa-exclamation-triangle fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;
                  <strong>{t('alerts.imbalanceWarningTitle')}</strong>{' '}
                  {t('alerts.imbalanceWarningText', {
                    difference: balanceDifference.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }),
                  })}
                  {firstImbalancedMonth && firstImbalancedYear && (
                    <span>
                      {' '}
                      {t('alerts.imbalanceOccurrenceNote', {
                        date: new Date(firstImbalancedYear, firstImbalancedMonth - 1).toLocaleString('en-US', {
                          month: 'long',
                          year: 'numeric',
                        }),
                      })}
                    </span>
                  )}
                </div>
              </Alert>
            </Snackbar>
          )
        )}
        {unreconciledPeriods.length > 0 && glBalanceSheetList.length > 0 && (
          <div className="bs-alert" role="status">
            <div className="bs-alert__icon" aria-hidden="true">
              <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
            <div className="bs-alert__body">
              <div>
                <span className="bs-alert__title">{t('alerts.unreconciledPeriodsTitle')}</span>
                <span className="bs-alert__text">
                  {t('alerts.unreconciledPeriodsText', { count: unreconciledPeriods.length })}
                </span>
              </div>
              <ul className="bs-alert__list">
                {Object.values(
                  unreconciledPeriods.reduce((acc: Record<string, { name: string; dates: string[] }>, item: any) => {
                    const key = String(item.bank?.id)
                    if (!acc[key]) {
                      acc[key] = {
                        name: `${item.bank?.account ?? ''} ${item.bank?.name ?? ''}`.trim(),
                        dates: [],
                      }
                    }
                    acc[key].dates.push(
                      new Date(item.period.year, item.period.month - 1).toLocaleString('en-US', {
                        month: 'short',
                        year: 'numeric',
                      })
                    )
                    return acc
                  }, {})
                ).map((bank) => (
                  <li key={bank.name}>
                    <span>
                      <span className="bs-alert__account">{bank.name}:</span> {bank.dates.reverse().join(', ')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <Link to={URLS.BANK_RECONCILE} className="bs-alert__action">
              {t('alerts.unreconciledPeriodsLink')}
              <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
            </Link>
          </div>
        )}
        <div className="bs-report-card">
          {glBalanceSheetList.length ? (
            <>
              <div className="bs-table" role="table" aria-label={t('title')} ref={tableContainerRef}>
                <div className="bs-row bs-row--head" role="row">
                  <div className="bs-cell bs-cell--desc" role="columnheader">
                    <span className="bs-col-label">{t('table.header.description')}</span>
                  </div>
                  <div className="bs-cell bs-cell--amount" role="columnheader">
                    <span className="bs-col-label">{generatedHeaders?.current.label}</span>
                    <span className="bs-col-date">{generatedHeaders?.current.date}</span>
                  </div>
                  <div className="bs-cell bs-cell--amount" role="columnheader">
                    <span className="bs-col-label">{generatedHeaders?.prior.label}</span>
                    <span className="bs-col-date">{generatedHeaders?.prior.date}</span>
                  </div>
                </div>
                {reportRows.map(({ data, code, level }) => {
                  const id = data['account_chart']['id']
                  const headerLevel = HEADER_LEVELS[code]
                  const isCollapsible = headerLevel !== undefined && headerLevel > 0
                  const showsAmounts = ACCOUNT_CODES.includes(code) || TOTAL_LEVELS[code] !== undefined
                  // View Register drill-down condition, kept exactly as before
                  const isStaticAmount =
                    (data['total_amount'] && data['total_amount'] == 0) || code !== 'VP' || !registerMonth
                  const levelStyle = { '--bs-level': level } as React.CSSProperties
                  return (
                    <div key={id} className={`bs-row bs-row--${ROW_VARIANTS[code] ?? 'account'}`} role="row">
                      <div className="bs-cell bs-cell--desc" role="rowheader" style={levelStyle}>
                        {isCollapsible ? (
                          <button
                            type="button"
                            className="bs-row-toggle"
                            aria-expanded={!collapsedIds.has(id)}
                            onClick={() => _toggleCollapsed(id)}
                          >
                            <i className="fa-solid fa-chevron-down bs-chevron" aria-hidden="true"></i>
                            {data['account_chart']['name']}
                          </button>
                        ) : (
                          data['account_chart']['name']
                        )}
                      </div>
                      <div className="bs-cell bs-cell--amount" role="cell">
                        {showsAmounts &&
                          renderAmount(
                            formatAmount(data['total_amount']),
                            isStaticAmount ? undefined : () => _handleShowViewRegister(data, true)
                          )}
                      </div>
                      <div className="bs-cell bs-cell--amount" role="cell">
                        {showsAmounts && renderAmount(formatAmount(data['prev_year_total_amount']))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            <div className="bs-empty">
              <i className="fa-regular fa-file-lines" aria-hidden="true"></i>
              {!lastGenerateEmpty ? t('table.select_period') : periodMode === 'custom' ? t('table.empty_custom') : t('table.empty')}
            </div>
          )}
        </div>
        <ScrollToggle scrollRef={tableContainerRef} data={glBalanceSheetList} />
      </div>

      {isShowViewRegister ? (
        <ViewRegisterModal
          show={isShowViewRegister}
          onHide={() => _handleCloseViewRegister()}
          startDate={txnRegisterPayload.startDate}
          endDate={txnRegisterPayload.endDate}
          account={txnRegisterPayload.account}
          hasEditAccess={txnRegisterPayload.hasEditAccess}
          header={t('modal.header', {
            monthYear: moment(txnRegisterPayload.startDate, 'YYYY-MM-DD').format('MMM, YYYY'),
          })}
          emptyMsg={t('modal.emptyMsg')}
          showBeginningAndEndingBalance={true}
        />
      ) : (
        <></>
      )}
    </div>
  )
}

export default BalanceSheet
